import clsx from 'clsx';
import { DateTime } from 'luxon';
import { observer } from 'mobx-react-lite';
import React from 'react';

import { getContrastTextColor } from '@/utils/colorConversions';
import { formatDurationRussian } from '@/utils/formatDurationRussian';

import type { EventBlockProps } from '../../types';

import s from './EventBlock.module.scss';

/** Approximate `color-mix(in srgb, color P%, white)` and return contrast text color */
function getTextColorForMixedBg(hexColor: string, mixPercent: number = 0.85): string {
  const cleanHex = hexColor.startsWith('#') ? hexColor.slice(1) : hexColor;
  const expanded =
    cleanHex.length === 3
      ? cleanHex
          .split('')
          .map((c) => c + c)
          .join('')
      : cleanHex;

  const r = parseInt(expanded.substring(0, 2), 16);
  const g = parseInt(expanded.substring(2, 4), 16);
  const b = parseInt(expanded.substring(4, 6), 16);

  const wp = 1 - mixPercent;
  const mixedR = Math.round(r * mixPercent + 255 * wp);
  const mixedG = Math.round(g * mixPercent + 255 * wp);
  const mixedB = Math.round(b * mixPercent + 255 * wp);

  const mixedHex = `#${mixedR.toString(16).padStart(2, '0')}${mixedG.toString(16).padStart(2, '0')}${mixedB.toString(16).padStart(2, '0')}`;

  return getContrastTextColor(mixedHex);
}

const EventBlock: React.FC<EventBlockProps> = observer(({ event, onClick }) => {
  const { top, height, left, width } = event;
  const { color, isOtherLog, otherLogData, collapsedGroup } = event.event;

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClick(event.event);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick(event.event);
    }
  };

  const baseStyle = {
    top: `${top}px`,
    height: `${height}px`,
    left: `${left}%`,
    width: `${width}%`,
    '--event-color': color,
  } as React.CSSProperties;

  // Collapsed other logs card
  if (isOtherLog && collapsedGroup) {
    return (
      <div
        className={clsx(s.event_block, s.event_block_collapsed)}
        style={baseStyle}
        onClick={handleClick}
        role="button"
        tabIndex={0}
        onKeyDown={handleKeyDown}
      >
        <div className={s.event_block__content}>
          <div className={s.event_block__collapsed_count}>
            ещё {collapsedGroup.collapsedLogs.length} · +
            {formatDurationRussian(collapsedGroup.totalCollapsedDuration)}
          </div>
        </div>
      </div>
    );
  }

  // Draft timelog event
  if (event.event.isDraft && event.event.draftData) {
    const { draftData } = event.event;
    const draftTextColor = getTextColorForMixedBg('#f59e0b', 0.85);
    const startTime = DateTime.fromISO(draftData.start);
    const endTime = startTime.plus({ minutes: draftData.durationMinutes });
    const timeRange = `${startTime.toFormat('HH:mm')} - ${endTime.toFormat('HH:mm')}`;

    return (
      <div
        className={clsx(s.event_block, s.event_block_draft)}
        style={{ ...baseStyle, '--event-text-color': draftTextColor } as React.CSSProperties}
        onClick={handleClick}
        role="button"
        tabIndex={0}
        onKeyDown={handleKeyDown}
      >
        <div className={s.event_block__content}>
          <div className={s.event_block__draft_badge}>Черновик</div>
          <div className={s.event_block__key}>
            {draftData.taskKey ?? draftData.taskTitle ?? '—'}
          </div>
          {height >= 40 && (
            <div className={s.event_block__duration}>
              {formatDurationRussian(draftData.durationMinutes)}
            </div>
          )}
          {height >= 56 && draftData.comment && (
            <div className={s.event_block__comment}>{draftData.comment}</div>
          )}
          {height >= 36 && <div className={s.event_block__time}>{timeRange}</div>}
        </div>
      </div>
    );
  }

  // Individual other log event
  if (isOtherLog && otherLogData) {
    // Background is `color-mix(in srgb, color 85%, white)` — compute matching text color
    const textColor = getTextColorForMixedBg(color, 0.85);

    return (
      <div
        className={clsx(s.event_block, s.event_block_other_log)}
        style={{ ...baseStyle, '--event-text-color': textColor } as React.CSSProperties}
        onClick={handleClick}
        role="button"
        tabIndex={0}
        onKeyDown={handleKeyDown}
      >
        <div className={s.event_block__content}>
          <div className={s.event_block__key}>{otherLogData.taskTitle}</div>
          {height >= 36 && (
            <div className={s.event_block__duration}>
              {formatDurationRussian(otherLogData.durationMinutes)}
            </div>
          )}
          {height >= 52 && otherLogData.comment && (
            <div className={s.event_block__comment}>{otherLogData.comment}</div>
          )}
        </div>
      </div>
    );
  }

  // Regular CalDav event
  const { summary, startsAt, endsAt, myResponseStatus } = event.event;

  const startTime = DateTime.fromISO(startsAt);
  const endTime = DateTime.fromISO(endsAt);
  const timeRange = `${startTime.toFormat('HH:mm')} - ${endTime.toFormat('HH:mm')}`;

  // Confirmation status: null or ACCEPTED = confirmed, TENTATIVE/NEEDS-ACTION = unconfirmed
  const isConfirmed = myResponseStatus === null || myResponseStatus === 'ACCEPTED';

  return (
    <div
      className={clsx(s.event_block, {
        [s.event_block_confirmed]: isConfirmed,
      })}
      style={baseStyle}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      <div className={s.event_block__content}>
        <div className={s.event_block__title}>{summary}</div>
        {height >= 40 && <div className={s.event_block__time}>{timeRange}</div>}
      </div>
    </div>
  );
});

export { EventBlock };
